import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { CreditNote } from '../../../types/accounting';

const BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

interface CreditNoteContextType {
  creditNotes: CreditNote[];
  loading: boolean;
  createLoading: boolean;
  fetchCreditNotes: () => Promise<void>;
  createCreditNote: (payload: CreateCreditNotePayload) => Promise<void>;
  deleteCreditNote: (id: string) => Promise<void>;
}

interface CreateCreditNotePayload {
  contactId?: string;
  contactName: string;
  total: number;
  date?: string;
  lineItems?: Array<{ description: string; quantity: number; unitAmount: number; taxType?: string }>;
  allocations?: Array<{ invoiceId: string; amount: number }>;
}

const CreditNoteContext = createContext<CreditNoteContextType | null>(null);

export function useCreditNote(): CreditNoteContextType {
  const ctx = useContext(CreditNoteContext);
  if (!ctx) throw new Error('useCreditNote must be used within CreditNoteProvider');
  return ctx;
}

export function CreditNoteProvider({ children }: { children: ReactNode }) {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const fetchCreditNotes = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${BASE}/listCreditNotesHttp`, { method: 'GET' });
      const data = await resp.json();
      if (data.ok) {
        setCreditNotes(data.creditNotes || []);
      }
    } catch (e) {
      console.error('[CreditNoteContext] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const createCreditNote = useCallback(async (payload: CreateCreditNotePayload) => {
    setCreateLoading(true);
    try {
      const resp = await authFetch(`${BASE}/createCreditNoteHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Create failed');
      await fetchCreditNotes();
    } finally {
      setCreateLoading(false);
    }
  }, [fetchCreditNotes]);

  const deleteCreditNote = useCallback(async (id: string) => {
    try {
      const resp = await authFetch(`${BASE}/deleteCreditNoteHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creditNoteId: id }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Delete failed');
      await fetchCreditNotes();
    } catch (e) {
      console.error('[CreditNoteContext] delete failed', e);
      throw e;
    }
  }, [fetchCreditNotes]);

  return (
    <CreditNoteContext.Provider
      value={{
        creditNotes,
        loading,
        createLoading,
        fetchCreditNotes,
        createCreditNote,
        deleteCreditNote,
      }}
    >
      {children}
    </CreditNoteContext.Provider>
  );
}
